import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import axiosInstance from '../utils/axiosConfig';
import '../styles/KasaYonetimi.css';
import '../styles/FinansalOzet.css';
import TrDateInput from '../components/TrDateInput';

ChartJS.register(ArcElement, Tooltip, Legend);

interface Kasa {
  kasaID: number;
  kasaAdi: string;
  kasaTuru: string;
  bakiye: number;
  paraBirimi: {
    paraBirimiID: number;
    birim: string;
  } | null;
}

interface KasaHareket {
  hareketID: number;
  kasaID: number;
  islemTuru: string;
  miktar: number;
  tarih: string;
  projeID?: number;
  cariID?: number;
  odemeID?: number;
  kategoriID?: number;
  aciklama?: string;
  olusturmaTarihi: string;
  kasa?: {
    kasaID: number;
    kasaAdi: string;
    kasaTuru: string;
  };
  proje?: {
    projeID: number;
    projeAdi: string;
  };
  cari?: {
    cariID: number;
    adiSoyadi: string;
  };
  kategori?: {
    kategoriID: number;
    kategoriAdi: string;
    tur: string;
  };
  ekleyen?: {
    kullaniciID: number;
    adSoyad: string;
  };
}

interface ParaBirimiToplam {
  birim: string;
  toplam: number;
}

interface DashboardData {
  nakitKasalar: Kasa[];
  bankaKasalar: Kasa[];
  toplamNakit: number;
  toplamBanka: number;
  genelToplam: number;
  nakitParaBirimleri: ParaBirimiToplam[];
  bankaParaBirimleri: ParaBirimiToplam[];
  genelParaBirimleri: ParaBirimiToplam[];
}

interface Odeme {
  odemeID: number;
  miktar: number;
  odemeTuru: string;
  odemeYonu: string;
  odemeDurumu: string;
  aktifMi: boolean;
  islemTakipNo: string;
  vadeTarihi: string;
  islemTarihi: string;
  aciklama?: string;
  ilgiliProje?: {
    projeID: number;
    projeAdi: string;
  } | null;
  cari: {
    cariID: number;
    adiSoyadi: string;
  } | null;
  paraBirimi: {
    paraBirimiID: number;
    birim: string;
  } | null;
  kasaID: number | null;
}

interface Proje {
  projeID: number;
  projeAdi: string;
}

interface Cari {
  cariID: number;
  adiSoyadi: string;
}

interface Kategori {
  kategoriID: number;
  kategoriAdi: string;
  tur: string;
}

const formatMoneyInput = (value: string): string => {
  const numbers = value.replace(/[^\d]/g, '');
  if (numbers === '') return '';
  return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseMoneyInput = (value: string): string => {
  return value.replace(/\./g, '');
};

const FinansalOzet: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedKasa, setSelectedKasa] = useState<Kasa | null>(null);
  const [hareketler, setHareketler] = useState<KasaHareket[]>([]);
  const [projeler, setProjeler] = useState<Proje[]>([]);
  const [cariler, setCariler] = useState<Cari[]>([]);
  const [kategoriler, setKategoriler] = useState<Kategori[]>([]);

  // Finansal özet kartları
  const [piyasaBorcu, setPiyasaBorcu] = useState(0);
  const [verilenAvans, setVerilenAvans] = useState(0);
  const [tahsilEdilecek, setTahsilEdilecek] = useState(0);

  // Gider dağılımı
  const [giderKategorileri, setGiderKategorileri] = useState<{ kategori: string; toplam: number }[]>([]);
  const [giderProjeler, setGiderProjeler] = useState<{ proje: string; toplam: number; kategoriler: string }[]>([]);

  // Vadesi geçmiş ödemeler
  const [gecikmisAlacakToplam, setGecikmisAlacakToplam] = useState(0);
  const [gecikmisAlacakMusteriSayisi, setGecikmisAlacakMusteriSayisi] = useState(0);

  // Banka bakiye düzenleme
  const [showBankaModal, setShowBankaModal] = useState(false);
  const [bankaBakiyeForm, setBankaBakiyeForm] = useState('');
  const [kasaActiveTab, setKasaActiveTab] = useState<'ozet' | 'nakit' | 'banka'>('ozet');

  // Tüm nakit hareketler (işlem listesi)
  const [tumHareketler, setTumHareketler] = useState<KasaHareket[]>([]);
  const [hareketFiltre, setHareketFiltre] = useState({
    baslangic: '',
    bitis: '',
    kasaID: ''
  });

  // Form state (Nakit kasalar için)
  const [formData, setFormData] = useState({
    kasaID: 0,
    kasaTuru: '',
    islemTuru: 0, // 0: Giriş, 1: Çıkış
    miktar: '',
    tarih: new Date().toISOString().split('T')[0],
    islemTipi: '', // 'cari' veya 'kategori' (sadece Nakit için)
    cariID: '',
    projeID: '',
    kategoriID: '',
    aciklama: ''
  });

  useEffect(() => {
    fetchDashboard();
    fetchProjeler();
    fetchCariler();
    fetchKategoriler();
    fetchTumHareketler();
    fetchOdemeler();
    fetchFinansalKpi();
    fetchGiderDagilimi();
  }, []);

  // Tüm finansal KPI'ları tek API çağrısıyla backend'den al
  // (Eski yöntem: 2 ayrı çağrı + frontend filter/reduce → artık kullanılmıyor)
  const fetchFinansalKpi = async () => {
    try {
      const response = await axiosInstance.get('/api/Odemeler/finansal-ozet-kpi');
      const kpi = response.data;
      setPiyasaBorcu(kpi.piyasaBorcu ?? 0);
      setVerilenAvans(kpi.verilenAvans ?? 0);
      setGecikmisAlacakToplam(kpi.gecikmisAlacakToplam ?? 0);
      setGecikmisAlacakMusteriSayisi(kpi.gecikmisAlacakMusteriSayisi ?? 0);
      setTahsilEdilecek(kpi.tahsilEdilecek ?? 0);
    } catch (error) {
      console.error('Finansal KPI yüklenirken hata:', error);
    }
  };

  const fetchGiderDagilimi = async () => {
    try {
      const response = await axiosInstance.get('/api/Odemeler/gider-dagilimi');
      setGiderKategorileri(response.data.kategoriler);
      setGiderProjeler(response.data.projeler);
    } catch (error) {
      console.error('Gider dağılımı yüklenirken hata:', error);
    }
  };

  const fetchOdemeler = async () => {
    try {
      const response = await axiosInstance.get('/api/Odemeler/tahsil-edilecek');
      setTahsilEdilecek(response.data.toplam);
    } catch (error) {
      console.error('Ödemeler yüklenirken hata:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      // Kasa listesini al (ad, tür, para birimi, banka bakiye bilgisi için)
      const dashRes = await axiosInstance.get('/api/KasaHareketleri/dashboard');
      const rawDash: DashboardData = dashRes.data;

      // NAKİT kasaların bakiyelerini backend'den al (Odemeler + KasaHareketleri birleşik)
      const nakitBakiyeRes = await axiosInstance.get('/api/KasaHareketleri/nakit-bakiyeler');
      const nakitBakiyeler: { kasaID: number; bakiye: number }[] = nakitBakiyeRes.data;
      const kasaBakiyeMap = new Map<number, number>(nakitBakiyeler.map(b => [b.kasaID, b.bakiye]));

      // NAKİT kasalar: bakiye Odemeler'den hesaplanır
      const nakitKasalar = rawDash.nakitKasalar.map(k => ({
        ...k,
        bakiye: kasaBakiyeMap.get(k.kasaID) ?? 0
      }));

      // BANKA kasalar: bakiye manuel girildiği için Kasalar.Bakiye korunur (rawDash'ten gelir)
      const bankaKasalar = rawDash.bankaKasalar;

      // Para birimi bazlı toplamları yeniden hesapla
      const grupla = (kasaListesi: Kasa[]) => {
        const map = new Map<string, number>();
        for (const k of kasaListesi) {
          const birim = k.paraBirimi?.birim ?? 'TRY';
          map.set(birim, (map.get(birim) ?? 0) + k.bakiye);
        }
        return Array.from(map.entries()).map(([birim, toplam]) => ({ birim, toplam }));
      };

      setDashboard({
        ...rawDash,
        nakitKasalar,
        bankaKasalar,
        toplamNakit: nakitKasalar.reduce((s, k) => s + k.bakiye, 0),
        toplamBanka: bankaKasalar.reduce((s, k) => s + k.bakiye, 0),
        genelToplam: [...nakitKasalar, ...bankaKasalar].reduce((s, k) => s + k.bakiye, 0),
        nakitParaBirimleri: grupla(nakitKasalar),
        bankaParaBirimleri: grupla(bankaKasalar),
        genelParaBirimleri: grupla([...nakitKasalar, ...bankaKasalar]),
      });
      setLoading(false);
    } catch (error) {
      console.error('Dashboard yüklenirken hata:', error);
      toast.error(t('msg.islemHatasi'));
      setLoading(false);
    }
  };

  const fetchProjeler = async () => {
    try {
      const response = await axiosInstance.get('/api/Projeler');
      setProjeler(response.data);
    } catch (error) {
      console.error('Projeler yüklenirken hata:', error);
    }
  };

  const fetchCariler = async () => {
    try {
      const response = await axiosInstance.get('/api/Cariler?tur=Cari');
      setCariler(response.data);
    } catch (error) {
      console.error('Cariler yüklenirken hata:', error);
    }
  };

  const fetchKategoriler = async () => {
    try {
      const response = await axiosInstance.get('/api/FinansKategorileri');
      setKategoriler(response.data);
    } catch (error) {
      console.error('Kategoriler yüklenirken hata:', error);
    }
  };

  const fetchTumHareketler = async (filtre?: { baslangic: string; bitis: string; kasaID: string }) => {
    try {
      const f = filtre || hareketFiltre;
      const params = new URLSearchParams();
      if (f.baslangic) params.append('baslangic', f.baslangic);
      if (f.bitis) params.append('bitis', f.bitis);
      if (f.kasaID) params.append('kasaID', f.kasaID);
      const response = await axiosInstance.get(`/api/KasaHareketleri/nakit-hareketler?${params.toString()}`);
      setTumHareketler(response.data);
    } catch (error) {
      console.error('Hareketler yüklenirken hata:', error);
    }
  };

  const handleYeniIslem = () => {
    setFormData({
      kasaID: 0,
      kasaTuru: 'Nakit',
      islemTuru: 0,
      miktar: '',
      tarih: new Date().toISOString().split('T')[0],
      islemTipi: 'cari',
      cariID: '',
      projeID: '',
      kategoriID: '',
      aciklama: ''
    });
    setSelectedKasa(null);
    setShowModal(true);
  };

  const handleKasaClick = async (kasa: Kasa) => {
    // Banka hesapları için hareket geçmişi yok
    if (kasa.kasaTuru === 'Banka') {
      toast.info(t('msg.bilgi'));
      return;
    }

    try {
      const response = await axiosInstance.get(`/api/KasaHareketleri/kasa/${kasa.kasaID}`);
      setHareketler(response.data);
      setSelectedKasa(kasa);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Hareket geçmişi yüklenirken hata:', error);
      toast.error(t('msg.islemHatasi'));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validasyon
    if (!formData.kasaID) {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }

    if (!formData.miktar || parseFloat(parseMoneyInput(formData.miktar)) <= 0) {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }

    // Banka hesapları için açıklama zorunlu
    if (formData.kasaTuru === 'Banka' && !formData.aciklama) {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }

    // Nakit kasalar için özel validasyonlar
    if (formData.kasaTuru === 'Nakit') {
      if (!formData.islemTipi) {
        toast.error(t('msg.zorunluAlanlar'));
        return;
      }
      
      if (formData.islemTipi === 'cari' && !formData.cariID) {
        toast.error(t('msg.zorunluAlanlar'));
        return;
      }

      if (formData.islemTipi === 'kategori' && !formData.kategoriID) {
        toast.error(t('msg.zorunluAlanlar'));
        return;
      }
    }

    try {
      const requestData = {
        kasaID: formData.kasaID,
        islemTuru: formData.islemTuru,
        miktar: parseFloat(parseMoneyInput(formData.miktar)),
        tarih: formData.tarih,
        islemTipi: formData.islemTipi || null,
        cariID: formData.cariID ? parseInt(formData.cariID) : null,
        projeID: formData.projeID ? parseInt(formData.projeID) : null,
        kategoriID: formData.kategoriID ? parseInt(formData.kategoriID) : null,
        aciklama: formData.aciklama || null
      };

      const response = await axiosInstance.post('/api/KasaHareketleri', requestData);
      
      // Backend'den gelen mesajı göster (banka için farklı response)
      const successMessage = response.data.message || 'İşlem başarıyla kaydedildi';
      toast.success(successMessage);
      
      setShowModal(false);
      fetchDashboard();
      fetchTumHareketler();
    } catch (error: any) {
      console.error('İşlem kaydedilirken hata:', error);
      const errorMessage = error.response?.data?.message || 'İşlem kaydedilemedi';
      toast.error(errorMessage);
    }
  };

  const handleDeleteHareket = async (hareket: KasaHareket) => {
    let confirmMsg = 'Bu işlemi silmek istediğinizden emin misiniz?';
    if (hareket.odemeID) {
      confirmMsg = `Bu kasa hareketi bir ödeme kaydı ile ilişkilidir.\n\n` +
        `Cari: ${hareket.cari?.adiSoyadi || '—'}\n` +
        `Miktar: ${hareket.miktar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}\n\n` +
        `Silme işlemi ile ilişkili ödeme kaydı da silinecektir. Devam etmek istiyor musunuz?`;
    }
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      await axiosInstance.delete(`/api/KasaHareketleri/${hareket.hareketID}`);
      toast.success(hareket.odemeID ? 'İşlem ve ilişkili ödeme kaydı silindi' : 'İşlem silindi');
      if (selectedKasa) {
        handleKasaClick(selectedKasa);
      }
      fetchDashboard();
      fetchTumHareketler();
    } catch (error) {
      console.error('İşlem silinirken hata:', error);
      toast.error(t('msg.islemHatasi'));
    }
  };

  // Banka bakiye modal aç
  const handleBankaDuzenle = (kasa: Kasa) => {
    setSelectedKasa(kasa);
    setBankaBakiyeForm(formatMoneyInput(kasa.bakiye.toString()));
    setShowBankaModal(true);
  };

  // Banka bakiye güncelleme
  const handleBankaBakiyeKaydet = async () => {
    if (!selectedKasa) return;

    if (bankaBakiyeForm === '') {
      toast.error(t('msg.zorunluAlanlar'));
      return;
    }

    try {
      const response = await axiosInstance.post(`/api/Kasalar/${selectedKasa.kasaID}/bakiye-guncelle`, {
        bakiye: parseFloat(parseMoneyInput(bankaBakiyeForm))
      });
      toast.success(response.data.message  || t('msg.islemBasarili'));
      setShowBankaModal(false);
      fetchDashboard();
    } catch (error: any) {
      console.error('Bakiye güncellenirken hata:', error);
      const errorMessage = error.response?.data?.message || 'Bakiye güncellenemedi';
      toast.error(errorMessage);
    }
  };

  const formatAmount = (val: number) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(val);

  if (loading) {
    return (
      <div className="content-wrapper kasa-page">
        <div className="kasa-header">
          <div className="kasa-header-inner">
            <div className="kasa-header-left">
              <span className="kasa-header-icon" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, width: 40, height: 40 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#cd6906" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
              </span>
              <div>
                <h1>{t('finans.baslik')}</h1>
                <p className="kasa-header-sub">{t('finans.altBaslik')}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="kasa-content">
          <div className="kasa-summary-strip">
            {[1, 2, 3].map((i) => (
              <div key={i} className="kasa-skeleton-card">
                <div className="kasa-skeleton kasa-skeleton-line w-40" />
                <div className="kasa-skeleton kasa-skeleton-line w-80 h-28" />
              </div>
            ))}
          </div>
          <div className="kasa-panel">
            <div className="kasa-panel-header"><span className="kasa-panel-title"><div className="kasa-skeleton kasa-skeleton-line w-40 h-18" style={{ marginBottom: 0 }} /></span></div>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9' }}>
                <div className="kasa-skeleton kasa-skeleton-line w-60 h-18" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: 24, fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>

      {/* ── Page Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.4px', margin: 0 }}>{t('finans.baslik')}</h1>
          <p style={{ fontSize: 13.5, color: '#64748b', margin: 0 }}>{t('finans.sayfaAciklama')}</p>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          BÖLÜM 1 — Proje Bazında Giderler (ÖNCELIKLI)
      ══════════════════════════════════════════════ */}
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 16, boxShadow: '0 1px 4px rgba(15,23,42,0.07)', padding: '24px 28px', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="2" width="16" height="20" rx="2"/>
                <path d="M9 22v-4h6v4"/>
                <path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{t('finans.projeBazindaGider')}</div>
            </div>
          </div>
        </div>

        {giderProjeler.length > 0 ? (() => {
          const maxToplam = Math.max(...giderProjeler.map(p => p.toplam));
          const barColors = ['#f59e0b', '#3b82f6', '#10b981', '#a78bfa', '#fb7185', '#38bdf8'];
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {giderProjeler.map((p, i) => {
                const pct = Math.round((p.toplam / maxToplam) * 100);
                return (
                  <div key={p.proje}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: barColors[i % 6], flexShrink: 0, display: 'inline-block' }} />
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{p.proje}</div>
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>{p.kategoriler}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>{formatAmount(p.toplam)}</div>
                        <div style={{ fontSize: 11.5, color: '#94a3b8' }}>%{pct}</div>
                      </div>
                    </div>
                    <div style={{ height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: barColors[i % 6], borderRadius: 99, transition: 'width 0.6s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })() : (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px 0', fontSize: 13.5 }}>{t('finans.projeGideriBulunamadi')}</div>
        )}
      </div>

      {/* ══════════════════════════════════════════════
          BÖLÜM 3 — Vadesi Geçmiş (Makro) + Finansal Kartlar
      ══════════════════════════════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16, marginBottom: 24, alignItems: 'start' }}>

        {/* Vadesi Geçmiş — Makro Özet */}
        <div style={{ background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 14, padding: '20px 22px', boxShadow: '0 1px 3px rgba(15,23,42,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="#dc2626" width="15" height="15"><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l2.5 2.5" /></svg>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: '#dc2626' }}>{t('finans.vadesiGecmis')}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 500, marginBottom: 3 }}>{t('finans.gecikenAlacak')}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#16a34a', letterSpacing: '-0.3px', fontVariantNumeric: 'tabular-nums' }}>
                {formatAmount(gecikmisAlacakToplam)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1, textAlign: 'center', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '6px 0' }}>
                <div style={{ fontSize: 11, color: '#64748b' }}>{t('finans.musteri')}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#16a34a' }}>{gecikmisAlacakMusteriSayisi}</div>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/tahsilat-performans')}
            style={{ width: '100%', padding: '9px 0', border: '1.5px solid #dc2626', borderRadius: 8, background: 'transparent', color: '#dc2626', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#dc2626'; }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" width="13" height="13"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
            {t('finans.detaylariGor')}
          </button>
        </div>

        {/* Piyasa Borcu / Tahsil Edilecek */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
          <div className="finansal-ozet-card borc">
            <div className="finansal-ozet-card-top">
              <span className="finansal-ozet-card-label">{t('finans.piyasaBorcu')}</span>
              <span className="finansal-ozet-card-icon borc">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
              </span>
            </div>
            <div className="finansal-ozet-card-amount">{formatAmount(piyasaBorcu)}</div>
            <div className="finansal-ozet-card-desc">{t('finans.piyasaBorcuAciklama')}</div>
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #fecaca' }}>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>{t('finans.verilenAvans')}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: verilenAvans > 0 ? '#10b981' : '#94a3b8' }}>{verilenAvans > 0 ? formatAmount(verilenAvans) : '—'}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{t('finans.borcBakiyeTedarikci')}</div>
            </div>
          </div>
          <div className="finansal-ozet-card tahsilat">
            <div className="finansal-ozet-card-top">
              <span className="finansal-ozet-card-label">{t('finans.tahsilEdilecek')}</span>
              <span className="finansal-ozet-card-icon tahsilat">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
              </span>
            </div>
            <div className="finansal-ozet-card-amount">{formatAmount(tahsilEdilecek)}</div>
            <div className="finansal-ozet-card-desc">{t('finans.tahsilEdilecekAciklama')}</div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          BÖLÜM 4 — Gider Dağılımı + Kasa Hesapları
      ══════════════════════════════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16, marginBottom: 24 }}>
        {/* Gider Dağılımı */}
        <div className="gider-dagilimi-panel" style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14, boxShadow: '0 1px 3px rgba(15,23,42,0.06)', padding: 24 }}>
          <h3 className="gider-dagilimi-title" style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 16 }}>{t('finans.giderDagilimi')}</h3>
          {giderKategorileri.length > 0 ? (
            <>
              <div className="gider-dagilimi-chart">
                <Doughnut
                  data={{
                    labels: giderKategorileri.map(g => g.kategori),
                    datasets: [{ data: giderKategorileri.map(g => g.toplam), backgroundColor: ['#34d399','#fbbf24','#60a5fa','#a78bfa','#fb7185','#38bdf8','#4ade80','#f97316'], borderWidth: 0 }],
                  }}
                  options={{ responsive: true, maintainAspectRatio: true, cutout: '65%', plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.parsed.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` } } } }}
                />
              </div>
              <div className="gider-dagilimi-legend">
                {giderKategorileri.map((g, i) => (
                  <div className="gider-legend-item" key={g.kategori}>
                    <span className="gider-legend-dot" style={{ background: ['#34d399','#fbbf24','#60a5fa','#a78bfa','#fb7185','#38bdf8','#4ade80','#f97316'][i % 8] }}></span>
                    <span>{g.kategori}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1" stroke="#d1d5db" width="48" height="48" style={{ margin: '0 auto 12px', display: 'block' }}><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z"/><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z"/></svg>
              <p style={{ fontSize: 14, margin: 0 }}>{t('finans.henuzGiderYok')}</p>
            </div>
          )}
        </div>

        {/* Kasa Hesapları (Özet / Nakit / Banka tabs) */}
        {dashboard && (
          <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14, boxShadow: '0 1px 3px rgba(15,23,42,0.06)', padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{t('finans.hesapBakiyeleri')}</h3>
              <div style={{ display: 'inline-flex', background: 'rgba(241,245,249,0.9)', padding: 3, borderRadius: 10, gap: 2 }}>
                {(['ozet', 'nakit', 'banka'] as const).map((tab) => {
                  const labels: Record<string, string> = { ozet: t('kasa.tabOzet'), nakit: t('kasa.nakit'), banka: t('kasa.banka') };
                  const isActive = kasaActiveTab === tab;
                  return (
                    <button key={tab} onClick={() => setKasaActiveTab(tab)} style={{ padding: '5px 12px', borderRadius: 7, border: 'none', fontSize: 12, fontWeight: isActive ? 600 : 500, cursor: 'pointer', fontFamily: 'inherit', color: isActive ? '#111827' : '#6b7280', background: isActive ? '#ffffff' : 'transparent', boxShadow: isActive ? '0 1px 3px rgba(15,23,42,0.10)' : 'none' }}>
                      {labels[tab]}
                    </button>
                  );
                })}
              </div>
            </div>

            {kasaActiveTab === 'ozet' && (
              <div style={{ display: 'flex', gap: 12 }}>
                {[
                  { label: t('kasa.toplamNakit'), items: dashboard.nakitParaBirimleri, color: '#fff7ed', border: '#fed7aa', stroke: '#cd6906' },
                  { label: t('kasa.toplamBanka'), items: dashboard.bankaParaBirimleri, color: '#eff6ff', border: '#bfdbfe', stroke: '#2563eb' },
                  { label: t('kasa.genelToplam'), items: dashboard.genelParaBirimleri, color: '#f0fdf4', border: '#bbf7d0', stroke: '#16a34a' },
                ].map(({ label, items, color, border, stroke }) => (
                  <div key={label} style={{ flex: 1, background: color, border: `1px solid ${border}`, borderRadius: 10, padding: '14px 16px' }}>
                    <div style={{ fontSize: 11.5, fontWeight: 500, color: '#6b7280', marginBottom: 10 }}>{label}</div>
                    {items && items.length > 0 ? items.map((pb) => (
                      <div key={pb.birim} style={{ fontSize: 14, fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>{formatAmount(pb.toplam)}</div>
                    )) : <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>₺0,00</div>}
                  </div>
                ))}
              </div>
            )}

            {kasaActiveTab === 'nakit' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {dashboard.nakitKasalar && dashboard.nakitKasalar.length > 0 ? dashboard.nakitKasalar.map((kasa) => (
                  <div key={kasa.kasaID} onClick={() => handleKasaClick(kasa)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 10, border: '1px solid #f1f5f9', cursor: 'pointer', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#d1d5db'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#f1f5f9'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="#cd6906" width="14" height="14"><path strokeLinecap="round" strokeLinejoin="round" d="M21 12V7H5a2 2 0 010-4h14v4M3 5v14a2 2 0 002 2h16v-5M18 12a2 2 0 000 4h4v-4z" /></svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{kasa.kasaAdi}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>{t('kasa.nakitKasa')}</div>
                      </div>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: kasa.bakiye >= 0 ? '#111827' : '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{formatAmount(kasa.bakiye)}</span>
                  </div>
                )) : <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20, fontSize: 13 }}>{t('kasa.nakitKasaBulunamadi')}</div>}
              </div>
            )}

            {kasaActiveTab === 'banka' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {dashboard.bankaKasalar && dashboard.bankaKasalar.length > 0 ? dashboard.bankaKasalar.map((kasa) => (
                  <div key={kasa.kasaID} onClick={() => handleBankaDuzenle(kasa)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 10, border: '1px solid #f1f5f9', cursor: 'pointer', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#d1d5db'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#f1f5f9'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="#2563eb" width="14" height="14"><rect x="3" y="3" width="18" height="18" rx="2" /><path strokeLinecap="round" strokeLinejoin="round" d="M3 9h18M9 21V9" /></svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{kasa.kasaAdi}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>Banka • {kasa.paraBirimi?.birim ?? 'TL'}</div>
                      </div>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: kasa.bakiye >= 0 ? '#111827' : '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{formatAmount(kasa.bakiye)}</span>
                  </div>
                )) : <div style={{ textAlign: 'center', color: '#9ca3af', padding: 20, fontSize: 13 }}>{t('kasa.bankaHesabiBulunamadi')}</div>}
              </div>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <div className="kasa-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="kasa-modal lg" onClick={(e) => e.stopPropagation()}>
            <div className="kasa-modal-header">
              <h5>{t('kasa.yeniIslem')}</h5>
              <button className="kasa-modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="kasa-modal-body">
                {/* Kasa Seçimi */}
                <div className="kasa-form-group">
                  <label className="kasa-form-label">{t('kasa.kasa')} <span className="required">*</span></label>
                  <select
                    className="kasa-form-select"
                    value={formData.kasaID}
                    onChange={(e) => {
                      const kasaId = parseInt(e.target.value);
                      const kasa = dashboard?.nakitKasalar.find(k => k.kasaID === kasaId) || null;
                      setSelectedKasa(kasa);
                      setFormData({ ...formData, kasaID: kasaId, kasaTuru: 'Nakit' });
                    }}
                    required
                  >
                    <option value={0}>{t('kasa.kasaSec')}</option>
                    {dashboard?.nakitKasalar.map(kasa => (
                      <option key={kasa.kasaID} value={kasa.kasaID}>
                        {kasa.kasaAdi} — {formatAmount(kasa.bakiye)} {kasa.paraBirimi?.birim ?? 'TL'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* İşlem Türü */}
                <div className="kasa-form-group">
                  <label className="kasa-form-label">{t('kasa.islemTuru')} <span className="required">*</span></label>
                  <div className="kasa-toggle-group">
                    <button
                      type="button"
                      className={`kasa-toggle-btn ${formData.islemTuru === 0 ? 'active-success' : ''}`}
                      onClick={() => setFormData({ ...formData, islemTuru: 0, kategoriID: '' })}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg> {t('kasa.girisGelir')}
                    </button>
                    <button
                      type="button"
                      className={`kasa-toggle-btn ${formData.islemTuru === 1 ? 'active-danger' : ''}`}
                      onClick={() => setFormData({ ...formData, islemTuru: 1, kategoriID: '' })}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg> {t('kasa.cikisGider')}
                    </button>
                  </div>
                </div>

                {/* Nakit — İşlem Tipi */}
                {formData.kasaTuru === 'Nakit' && (
                  <div className="kasa-form-group">
                    <label className="kasa-form-label">{t('kasa.islemTipi')} <span className="required">*</span></label>
                    <div className="kasa-toggle-group">
                      <button
                        type="button"
                        className={`kasa-toggle-btn ${formData.islemTipi === 'cari' ? 'active-primary' : ''}`}
                        onClick={() => setFormData({ ...formData, islemTipi: 'cari', kategoriID: '' })}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> {t('kasa.cariIslemi')}
                      </button>
                      <button
                        type="button"
                        className={`kasa-toggle-btn ${formData.islemTipi === 'kategori' ? 'active-primary' : ''}`}
                        onClick={() => setFormData({ ...formData, islemTipi: 'kategori', cariID: '', projeID: '' })}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg> {t('kasa.genelGiderGelir')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Cari fields */}
                {formData.kasaTuru === 'Nakit' && formData.islemTipi === 'cari' && (
                  <>
                    <div className="kasa-form-group">
                      <label className="kasa-form-label">{t('kasa.cari')} <span className="required">*</span></label>
                      <select
                        className="kasa-form-select"
                        value={formData.cariID}
                        onChange={(e) => setFormData({ ...formData, cariID: e.target.value })}
                        required
                      >
                        <option value="">{t('kasa.cariSec')}</option>
                        {cariler.map((cari) => (
                          <option key={cari.cariID} value={cari.cariID}>{cari.adiSoyadi}</option>
                        ))}
                      </select>
                    </div>
                    <div className="kasa-form-group">
                      <label className="kasa-form-label">{t('kasa.proje')} <span className="optional">({t('kasa.opsiyonel')})</span></label>
                      <select
                        className="kasa-form-select"
                        value={formData.projeID}
                        onChange={(e) => setFormData({ ...formData, projeID: e.target.value })}
                      >
                        <option value="">{t('kasa.projeSec')}</option>
                        {projeler.map((proje) => (
                          <option key={proje.projeID} value={proje.projeID}>{proje.projeAdi}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* Kategori field */}
                {formData.kasaTuru === 'Nakit' && formData.islemTipi === 'kategori' && (
                  <div className="kasa-form-group">
                    <label className="kasa-form-label">{t('kasa.kategori')} <span className="required">*</span></label>
                    <select
                      className="kasa-form-select"
                      value={formData.kategoriID}
                      onChange={(e) => setFormData({ ...formData, kategoriID: e.target.value })}
                      required
                    >
                      <option value="">{t('kasa.kategoriSec')}</option>
                      {kategoriler
                        .filter((kategori) => formData.islemTuru === 0 ? kategori.tur === 'Gelir' : kategori.tur === 'Gider')
                        .map((kategori) => (
                        <option key={kategori.kategoriID} value={kategori.kategoriID}>
                          {kategori.kategoriAdi}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Miktar & Tarih */}
                <div className="kasa-form-row">
                  <div className="kasa-form-group">
                    <label className="kasa-form-label">{t('kasa.miktar')} <span className="required">*</span></label>
                    <input
                      type="text"
                      className="kasa-form-input"
                      value={formData.miktar}
                      onChange={(e) => setFormData({ ...formData, miktar: formatMoneyInput(e.target.value) })}
                      placeholder="0"
                      required
                    />
                  </div>
                  <div className="kasa-form-group">
                    <label className="kasa-form-label">{t('label.tarih')} <span className="required">*</span></label>
                    <TrDateInput
                      className="kasa-form-input"
                      value={formData.tarih}
                      onChange={(e) => setFormData({ ...formData, tarih: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* Açıklama */}
                <div className="kasa-form-group">
                  <label className="kasa-form-label">
                    {t('kasa.aciklama')} {formData.kasaTuru === 'Banka' && <span className="required">*</span>}
                  </label>
                  <textarea
                    className="kasa-form-textarea"
                    rows={3}
                    value={formData.aciklama}
                    onChange={(e) => setFormData({ ...formData, aciklama: e.target.value })}
                    placeholder={t('label.notlar')}
                    required={formData.kasaTuru === 'Banka'}
                  />
                </div>

                {/* Banka Uyarı */}
                {formData.kasaTuru === 'Banka' && (
                  <div className="kasa-warning-box">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span><strong>{t('kasa.bankaHesabiIslemi')}:</strong> {t('kasa.bankaHesabiIslemAciklama')}</span>
                  </div>
                )}
              </div>
              <div className="kasa-modal-footer">
                <button type="button" className="kasa-btn-cancel" onClick={() => setShowModal(false)}>{t('btn.iptal')}</button>
                <button type="submit" className="kasa-btn-save">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg> {t('btn.kaydet')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== Detay Modal ===== */}
      {showDetailModal && selectedKasa && (
        <div className="kasa-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="kasa-modal xl" onClick={(e) => e.stopPropagation()}>
            <div className="kasa-modal-header">
              <h5>{selectedKasa.kasaAdi} — {t('kasa.islemGecmisi')}</h5>
              <button className="kasa-modal-close" onClick={() => setShowDetailModal(false)}>&times;</button>
            </div>
            <div className="kasa-modal-body">
              <div className="kasa-detail-info">
                <span className="detail-kasa-name">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, opacity: 0.6 }}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                  {selectedKasa.kasaAdi}
                </span>
                <span className={`detail-bakiye ${selectedKasa.bakiye >= 0 ? '' : 'text-danger'}`} style={{ color: selectedKasa.bakiye >= 0 ? '#16a34a' : '#dc2626' }}>
                  {formatAmount(selectedKasa.bakiye)} {selectedKasa.paraBirimi?.birim}
                </span>
              </div>

              {hareketler.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table className="kasa-table">
                    <thead>
                      <tr>
                        <th>{t('label.tarih')}</th>
                        <th>{t('kasa.islem')}</th>
                        <th>{t('kasa.miktar')}</th>
                        <th>{t('kasa.cari')}</th>
                        <th>{t('kasa.proje')}</th>
                        <th>{t('kasa.kategori')}</th>
                        <th>{t('kasa.aciklama')}</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {hareketler.map((hareket) => (
                        <tr key={hareket.hareketID}>
                          <td style={{ whiteSpace: 'nowrap' }}>{new Date(hareket.tarih).toLocaleDateString('tr-TR')}</td>
                          <td>
                            {hareket.islemTuru === 'Giris' ? (
                              <span className="kasa-badge giris">{t('kasa.giris')}</span>
                            ) : (
                              <span className="kasa-badge cikis">{t('kasa.cikis')}</span>
                            )}
                          </td>
                          <td style={{ fontWeight: 600, fontFamily: "'JetBrains Mono','SF Mono','Consolas',monospace", fontVariantNumeric: 'tabular-nums', color: hareket.islemTuru === 'Giris' ? '#16a34a' : '#dc2626' }}>
                            {formatAmount(hareket.miktar)}
                          </td>
                          <td>{hareket.cari?.adiSoyadi || '—'}</td>
                          <td>{hareket.proje?.projeAdi || '—'}</td>
                          <td>{hareket.kategori?.kategoriAdi || '—'}</td>
                          <td>{hareket.aciklama || '—'}</td>
                          <td>
                            <button className="kasa-icon-btn delete" onClick={() => handleDeleteHareket(hareket)} title={t('btn.sil')}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="kasa-empty-state">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 8 }}><path d="M21 9V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2"/><line x1="3" y1="9" x2="21" y2="9"/></svg>
                  <p>{t('kasa.henuzIslemYok')}</p>
                </div>
              )}
            </div>
            <div className="kasa-modal-footer">
              <button className="kasa-btn-cancel" onClick={() => setShowDetailModal(false)}>{t('btn.kapat')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Banka Bakiye Düzenleme Modal ===== */}
      {showBankaModal && selectedKasa && (
        <div className="kasa-modal-overlay" onClick={() => setShowBankaModal(false)}>
          <div className="kasa-modal sm" onClick={(e) => e.stopPropagation()}>
            <div className="kasa-modal-header">
              <h5>{selectedKasa.kasaAdi} — {t('kasa.bakiyeDuzenle')}</h5>
              <button className="kasa-modal-close" onClick={() => setShowBankaModal(false)}>&times;</button>
            </div>
            <div className="kasa-modal-body">
              <div className="kasa-bakiye-mevcut">
                <span className="bakiye-label">{t('kasa.mevcutBakiye')}</span>
                <span className="bakiye-value">
                  {formatAmount(selectedKasa.bakiye)} {selectedKasa.paraBirimi?.birim}
                </span>
              </div>
              <div className="kasa-form-group">
                <label className="kasa-form-label">{t('kasa.yeniBakiye')} ({selectedKasa.paraBirimi?.birim}) <span className="required">*</span></label>
                <input
                  type="text"
                  className="kasa-form-input lg"
                  value={bankaBakiyeForm}
                  onChange={(e) => setBankaBakiyeForm(formatMoneyInput(e.target.value))}
                  autoFocus
                />
              </div>
            </div>
            <div className="kasa-modal-footer">
              <button className="kasa-btn-cancel" onClick={() => setShowBankaModal(false)}>{t('btn.iptal')}</button>
              <button className="kasa-btn-save" onClick={handleBankaBakiyeKaydet}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg> {t('btn.kaydet')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinansalOzet;
